import { setToastMuted, toast } from "../toast/toast";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Membership, Role, User } from "../../../backend/src/types";
import { useUser } from "../contexts/UserContext";
import {
  addUser,
  editUser,
  getRoleList,
  getUserMembership,
  resetUserPassword,
} from "../fetch/UserRequest";
import { useAlert } from "../confirm/ConfirmProvider";
import { checkSpace } from "../function/checkSpace";
import { errorMessage } from "../function/errorMessage";
import styles from "../styles/modules/AdminPage.module.css";
import RequiredMark from "./RequiredMark";
import Papa from "papaparse";
import { getAlertsCount } from "../fetch/AlertRequest";

interface UserPopupProps {
  targetUser: User | null;
  isCSVMode: boolean;
  onClose: (reload?: boolean) => void;
  onLoading: (start: boolean) => void;
  updateUserList: (updateUser: User) => void;
}

const UserPopup: React.FC<UserPopupProps> = ({
  targetUser,
  isCSVMode,
  onClose,
  onLoading,
  updateUserList,
}) => {
  const studentNoPattern = /^.{6,}$/;
  const isEdit = targetUser !== null;
  const [roles, setRoles] = useState<Role[]>([]);
  const [memberships, setMemberships_Popup] = useState<Membership[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedLabId, setSelectedLabId] = useState<number | null>(null);

  const [oldNo, setOldNo] = useState<string>("");

  const nameRef = useRef<HTMLInputElement>(null);
  const studentNoRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmRef = useRef<HTMLInputElement>(null);

  const { user, setUser, labs, setMemberships, setUserLabs, setAlertCount } =
    useUser();
  const showAlert = useAlert();
  const navigate = useNavigate();

  const selectedRole = roles.find((item) => item.id === selectedRoleId);
  const showLab =
    selectedRole?.code === "lab_admin" || selectedRole?.code === "lab_user";

  useEffect(() => {
    //大学管理者を選択したならnullに変更
    if (selectedRole?.code === "tenant_admin") setSelectedLabId(null);
  }, [selectedRoleId]);

  useEffect(() => {
    if (user) {
      InitEditModeFrom();
      loadUserData();
    }
  }, [user, targetUser]);

  function InitEditModeFrom() {
    if (
      targetUser !== null &&
      nameRef.current !== null &&
      studentNoRef.current !== null
    ) {
      nameRef.current.value = targetUser.name;
      studentNoRef.current.value = targetUser.student_no;
      setOldNo(targetUser.student_no);
    }
  }

  async function loadUserData() {
    onLoading(true);

    const rolesList = await getRoleList();

    setRoles(rolesList);

    if (targetUser !== null) {
      const membershipList = await getUserMembership({ targetUser });

      setMemberships_Popup(membershipList);
    }

    onLoading(false);
  }

  async function handleAddUser() {
    const name = nameRef.current?.value.trim() ?? "";
    const student_no = studentNoRef.current?.value.trim() ?? "";
    const password = passwordRef.current?.value ?? "";
    const passwordConfirm = passwordConfirmRef.current?.value ?? "";

    if (student_no === "" || password === "") {
      toast.error("学籍番号かパスワードが未入力です。");
      return;
    }

    if (checkSpace([password])) {
      toast.error("パスワードに空白を含めないでください。");
      return;
    }

    if (password.length < 8 || password.length > 32) {
      toast.error("パスワードは8〜32文字で入力してください。");
      return;
    }

    if (password !== passwordConfirm) {
      toast.error("パスワードと確認用パスワードが一致しません。");
      return;
    }

    if (!studentNoPattern.test(student_no)) {
      toast.error("学籍番号は6文字以上で入力してください。");
      return;
    }

    onLoading(true);

    const newUser: User = {
      id: 0,
      tenant_id: user?.tenant_id ?? 0,
      student_no,
      name,
      is_active: true,
      last_login_at: "",
      created_at: "",
      updated_at: "",
    };

    const result = await addUser({
      user: newUser,
      password,
      memberships,
    });

    if (result.ok) {
      updateUserList(result.user);
      toast.success(`${name}を登録しました。`);
      onClose();
    }

    onLoading(false);
  }

  async function handleEditUser() {
    if (targetUser === null) {
      errorMessage(
        "編集対象のユーザーを取得できませんでした",
        "error:editUser",
      );
      return;
    }

    const target_user_id = targetUser.id;
    const name = nameRef.current?.value.trim() ?? "";
    const student_no = studentNoRef.current?.value.trim() ?? "";
    // 任意：入力があればパスワードも変更する
    const newPassword = passwordRef.current?.value ?? "";
    const newPasswordConfirm = passwordConfirmRef.current?.value ?? "";

    if (student_no === "") {
      toast.error("学籍番号が未入力です。");
      return;
    }

    if (!studentNoPattern.test(student_no)) {
      toast.error("学籍番号は6文字以上で入力してください。");
      return;
    }

    // パスワード変更が入力されている場合は先に検証
    if (newPassword !== "" || newPasswordConfirm !== "") {
      if (newPassword.length < 8 || newPassword.length > 32) {
        toast.error("パスワードは8〜32文字で入力してください。");
        return;
      }
      if (checkSpace([newPassword])) {
        toast.error("パスワードに空白を含めないでください。");
        return;
      }
      if (newPassword !== newPasswordConfirm) {
        toast.error("パスワードと確認用パスワードが一致しません。");
        return;
      }
    }

    const newUser: User = {
      id: target_user_id,
      tenant_id: user?.tenant_id ?? 0,
      student_no,
      name,
      is_active: true,
      last_login_at: "",
      created_at: "",
      updated_at: "",
    };

    const isSameNo = newUser.student_no === oldNo;

    const updataUser = await editUser({
      user: newUser,
      memberships,
      isSameNo,
    });

    if (updataUser != null) {
      // パスワードが入力されていれば変更（大学管理者のみ実行可能なAPI）
      if (newPassword !== "") {
        try {
          await resetUserPassword(target_user_id, newPassword);
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : "パスワードの変更に失敗しました。",
          );
          onLoading(false);
          return;
        }
      }

      // 自分自身のパスワードを変更した場合は、セッションが無効化されている。
      // 追加APIは呼ばず（401回避）、再ログインを促してログイン画面へ遷移する。
      if (target_user_id === user?.id && newPassword !== "") {
        onLoading(false);
        await showAlert(
          "パスワードを変更しました。セキュリティのため、もう一度ログインしてください。",
          { title: "パスワード変更", closeText: "ログイン画面へ" },
        );
        localStorage.removeItem("sessionId");
        setUser(null);
        navigate("/login");
        return;
      }

      updateUserList(updataUser);
      if (target_user_id === user?.id) {
        setMemberships(memberships);

        const lab_idList = memberships
          .map((m) => m.lab_id)
          .filter((id): id is number => id !== null);

        const userLabList = labs.filter((lab) => lab_idList.includes(lab.id));

        const sortedLabList = Array.from(userLabList.values()).sort((a, b) =>
          a.name.localeCompare(b.name, "ja"),
        );

        setUserLabs(sortedLabList);

        if (user?.tenant_id) {
          const tenant_id = user?.tenant_id;
          const lab_ids = sortedLabList.map((lab) => lab.id);
          const alertCount = await getAlertsCount({ tenant_id, lab_ids });
          setAlertCount(alertCount);
        }
      }
      toast.success("ユーザーの更新処理に成功しました。");
      onClose();
    }

    onLoading(false);
  }

  async function addMembership() {
    try {
      if (!selectedRole || !selectedRoleId) {
        toast.error("権限を選択してください。");
        return;
      }

      if (selectedRole.code !== "tenant_admin" && !selectedLabId) {
        toast.error("大学管理者以外は研究室を選択してください。");
        return;
      }

      let exists = memberships.some(
        (item) =>
          item.role_code === selectedRole.code && item.lab_id === selectedLabId,
      );

      if (exists) {
        toast.error("同じ権限と研究室の組み合わせはすでに追加されています。");
        return;
      }

      if (
        selectedRole.code === "lab_admin" ||
        selectedRole.code === "lab_user"
      ) {
        let restrictRoleCode = "";
        if (selectedRole.code === "lab_admin") {
          restrictRoleCode = "lab_user";
        } else {
          restrictRoleCode = "lab_admin";
        }

        const restrictRole = roles.find(
          (role) => role.code === restrictRoleCode,
        );

        let exists = memberships.some(
          (item) =>
            item.role_code === restrictRole?.code &&
            item.lab_id === selectedLabId,
        );

        if (exists) {
          toast.error(
            "一つの研究室の管理者権限とユーザー権限を同時に持つことはできません。",
          );
          return;
        }
      }

      const newMembership: Membership = {
        //id,user_id,tenant_idは非nullを埋めるための一時的な値
        id: Date.now(),
        user_id: 0,
        tenant_id: 0,
        role_code: selectedRole.code,
        lab_id: selectedLabId,
      };

      setSelectedLabId(null);
      setSelectedRoleId(null);
      setMemberships_Popup((prev) => [...prev, newMembership]);
    } catch (error) {
      errorMessage("権限の追加処理でエラーが発生しました", error);
    }
  }

  function deleteMembership(id: number) {
    setMemberships_Popup((prev) => prev.filter((m) => m.id !== id));
  }

  function importCSV() {
    const input = document.getElementById("csvInput") as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      alert("CSVファイルを選択してください");
      return;
    }

    type CSVUser = Pick<User, "name" | "student_no"> & {
      password: string;
    };

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),

      complete: async function (results) {
        const rows = results.data as CSVUser[];

        // 除外行（原因付き）: 形式不備・サーバー失敗の両方をここへ集約する
        type ImportError = { index: number; name: string; reason: string };
        const localErrors: ImportError[] = [];
        const validEntries: {
          index: number;
          name: string;
          promise: ReturnType<typeof addUser>;
        }[] = [];

        // ローカル検証。最初に該当した原因を返す（通過なら null）。
        const localReason = (
          name: string,
          student_no: string,
          password: string,
        ): string | null => {
          if (!name) return "氏名が未入力です";
          if (!student_no) return "学籍番号が未入力です";
          if (!password) return "パスワードが未入力です";
          if (!studentNoPattern.test(student_no))
            return "学籍番号は6文字以上で入力してください";
          if (password.length < 8 || password.length > 32)
            return "パスワードは8〜32文字で入力してください";
          if (checkSpace([password]))
            return "パスワードに空白は使用できません";
          return null;
        };

        try {
          // 一括処理中は個別のトーストを抑制し、最後に原因付きで要約を出す
          setToastMuted(true);
          onLoading(true);

          rows.forEach((row, index) => {
            const name = row.name?.trim() ?? "";
            const student_no = row.student_no?.trim() ?? "";
            const password = row.password?.trim() ?? "";

            const reason = localReason(name, student_no, password);
            if (reason) {
              localErrors.push({
                index: index + 1,
                name: name || "(名前不明)",
                reason,
              });
              return;
            }

            const newUser: User = {
              id: 0,
              tenant_id: user?.tenant_id ?? 0,
              student_no,
              name,
              is_active: true,
              last_login_at: "",
              created_at: "",
              updated_at: "",
            };

            validEntries.push({
              index: index + 1,
              name,
              promise: addUser({ user: newUser, password, memberships }),
            });
          });

          const settled = await Promise.all(validEntries.map((e) => e.promise));

          // サーバー失敗（重複など）を、どの行か・原因付きで集約
          const serverErrors: ImportError[] = [];
          settled.forEach((result, i) => {
            if (result.ok) {
              updateUserList(result.user);
            } else {
              serverErrors.push({
                index: validEntries[i].index,
                name: validEntries[i].name,
                reason: result.reason,
              });
            }
          });

          // 要約表示の前に、ミュート解除とローディング終了をしておく
          // （モーダル待機中に裏でローディングが残らないように）
          setToastMuted(false);
          onLoading(false);

          const successCount = settled.length - serverErrors.length;
          const failed = [...localErrors, ...serverErrors].sort(
            (a, b) => a.index - b.index,
          );

          if (failed.length === 0) {
            // すべて成功したときは軽くトーストで通知し、ポップアップを閉じる
            toast.success(`${successCount}件のユーザーを登録しました。`);
            onClose(false);
          } else {
            // 失敗がある場合は、原因を読める形でモーダル表示する
            await showAlert(
              <div>
                {successCount > 0 && (
                  <p className={styles["import-success"]}>
                    {successCount}件のユーザーを登録しました。
                  </p>
                )}
                <p className={styles["import-fail-title"]}>
                  {failed.length}件は登録できませんでした：
                </p>
                <ul className={styles["import-fail-list"]}>
                  {failed.map((e) => (
                    <li key={e.index} className={styles["import-fail-item"]}>
                      <span className={styles["import-fail-row"]}>
                        {e.index}行目 / {e.name}
                      </span>
                      <span className={styles["import-fail-reason"]}>
                        {e.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>,
              { title: "CSVインポート結果" },
            );
          }
        } catch (err) {
          setToastMuted(false);
          errorMessage("一括登録エラー:", err);
          toast.error("CSVインポートに失敗しました。");
        } finally {
          onLoading(false);
          setToastMuted(false);
        }
      },
    });
  }

  const userPopUpContent = (
    <div className={styles["popup-background"]} onClick={() => onClose(false)}>
      <div
        className={styles["popup-section"]}
        onClick={(e) => e.stopPropagation()}
      >
        {!isCSVMode && (
          <h3>{isEdit ? "ユーザー情報の編集" : "ユーザーの登録"}</h3>
        )}
        {isCSVMode && (
          <div className={styles["csv-section"]}>
            <h3>CSVのインポート</h3>
            <p>
              ※CSVファイルを読み込むことで、ユーザー情報を一括登録できます。
              <br />
              CSVファイルの1行目（ヘッダー）はname, student_no,
              passwordを記述し、
              <br />
              その下にそれぞれの「名前」「学籍番号」「パスワード」を入力してください。
              <br />
              権限を追加している場合、全ての新規登録ユーザーにその権限が付与されます。
            </p>
          </div>
        )}
        <form
          onKeyDown={(e) => {
            // 入力欄での Enter による暗黙送信を防ぐ（登録/更新はボタン押下のみ）
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
          onSubmit={(e) => {
            e.preventDefault();
            isEdit ? handleEditUser() : handleAddUser();
          }}
        >
          {!isCSVMode && (
            <>
              <label>
                <span>
                  ユーザー名
                  <RequiredMark />
                </span>
                <input
                  type="text"
                  ref={nameRef}
                  placeholder="氏名を入力"
                  autoComplete="off"
                  required
                />
              </label>
              <label>
                <span>
                  学籍番号
                  <RequiredMark />
                </span>
                <input
                  type="text"
                  ref={studentNoRef}
                  pattern="^.{6,}$"
                  title="学籍番号は6文字以上で入力してください"
                  placeholder="6文字以上で入力"
                  autoComplete="off"
                  required
                />
              </label>
              {!isEdit && (
                <>
                  <label>
                    <span>
                      パスワード <RequiredMark />
                    </span>
                    <input
                      type="password"
                      ref={passwordRef}
                      minLength={8}
                      maxLength={32}
                      placeholder="8〜32文字で入力してください"
                      autoComplete="off"
                      required
                    />
                  </label>
                  <label>
                    <span>
                      パスワード（確認） <RequiredMark />
                    </span>
                    <input
                      type="password"
                      ref={passwordConfirmRef}
                      minLength={8}
                      maxLength={32}
                      placeholder="もう一度入力してください"
                      autoComplete="off"
                      required
                    />
                  </label>
                </>
              )}
              {isEdit && (
                <>
                  <label>
                    <span>新しいパスワード（変更する場合のみ）</span>
                    <input
                      type="password"
                      ref={passwordRef}
                      minLength={8}
                      maxLength={32}
                      placeholder="変更しない場合は空欄"
                      autoComplete="off"
                    />
                  </label>
                  <label>
                    <span>新しいパスワード（確認）</span>
                    <input
                      type="password"
                      ref={passwordConfirmRef}
                      minLength={8}
                      maxLength={32}
                      placeholder="変更する場合のみ入力"
                      autoComplete="off"
                    />
                  </label>
                </>
              )}
            </>
          )}
          <h3>権限管理</h3>
          {memberships.length === 0 ? (
            <p>権限なし</p>
          ) : (
            <div className={`${styles["list-section"]} ${styles["up-space"]}`}>
              <table>
                <thead>
                  <tr>
                    <th>権限</th>
                    <th>研究室</th>
                    <th>削除</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((membership) => {
                    const role = roles.find(
                      (item) => item.code === membership.role_code,
                    );
                    const lab = labs.find(
                      (item) => item.id === membership.lab_id,
                    );

                    return (
                      <tr key={membership.id}>
                        <td>{role?.label ?? "-"}</td>
                        <td>{lab?.name ?? "-"}</td>
                        <td>
                          <button
                            onClick={() => deleteMembership(membership.id)}
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <div>
              <label>
                <select
                  value={selectedRoleId ?? ""}
                  onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                >
                  <option value="">権限を選択</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </label>
              {showLab === true && (
                <label>
                  研究室
                  <select
                    value={selectedLabId ?? ""}
                    onChange={(e) => setSelectedLabId(Number(e.target.value))}
                  >
                    <option value="">研究室を選択</option>
                    {labs.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
          <div>
            <button type="button" onClick={addMembership}>
              追加
            </button>
          </div>
          {!isCSVMode && (
            <div className={styles["button-row"]}>
              <button type="submit" className={styles["ok-button"]}>
                {isEdit ? "更新" : "登録"}
              </button>
              <button type="button" onClick={() => onClose(false)}>
                キャンセル
              </button>
            </div>
          )}
        </form>
        {isCSVMode && (
          <div className={styles["csv-section"]}>
            <label>
              <h3>
                CSVファイルを選択
                <RequiredMark />
              </h3>
              <input type="file" id="csvInput" accept=".csv" />
            </label>
            <div className={styles["button-row"]}>
              <button
                className={styles["import-button"]}
                type="button"
                onClick={() => importCSV()}
              >
                インポート
              </button>
              <button type="button" onClick={() => onClose(false)}>
                キャンセル
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    userPopUpContent,
    document.getElementById("popup-root")!,
  );
};

export default UserPopup;
